import { useEffect, useRef, useState } from 'react';
import agent from '../../app/api/agent';
import type { SetupHealth } from '../../app/models/SetupHealth';
import { getDashboardSetupStatus } from '../setup/dashboardSetupStatus';
import { loadThemeActivationStatus, type ThemeActivationStatus } from '../setup/themeActivationStatus';

// This shop-scoped hint changes wording only. Visibility always uses fresh checks.
// Missing/blocked browser storage must not prevent setup checks from working.
const readyKey = (shop: string) => `partfits.setup-previously-ready:${shop.trim().toLowerCase()}`;
export const previouslyReady = (shop: string) => {
    try { return Boolean(shop) && localStorage.getItem(readyKey(shop)) === 'true'; } catch { return false; }
};

export default function useDashboardSetup() {
    const [result, setResult] = useState<{ health: SetupHealth; activations: ThemeActivationStatus }>();
    const [checked, setChecked] = useState(false);
    const health = result?.health;
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [reload, setReload] = useState(0);
    const themeEditorVisit = useRef<'idle' | 'opening' | 'away'>('idle');

    // Embedded admin focus can change during ordinary interactions and App Bridge
    // requests. Only refresh once after leaving for a theme-editor link we opened.
    useEffect(() => {
        const leave = () => {
            if (themeEditorVisit.current === 'opening') themeEditorVisit.current = 'away';
        };
        const recheck = () => {
            if (themeEditorVisit.current !== 'away') return;
            themeEditorVisit.current = 'idle';
            setReload(value => value + 1);
        };
        const onBlur = (event: FocusEvent) => { if (event.target === window) leave(); };
        const onFocus = (event: FocusEvent) => {
            if (event.target === window && document.visibilityState === 'visible') recheck();
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') leave();
            else if (document.visibilityState === 'visible') recheck();
        };
        window.addEventListener('blur', onBlur);
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setFailed(false);
        const load = async () => {
            try {
                // No selected theme ID: always ask for the published theme.
                const [result, themeActivations] = await Promise.all([
                    agent.Settings.setupHealth(),
                    loadThemeActivationStatus(),
                ]);
                if (cancelled) return;
                setResult({ health: result, activations: themeActivations });
                if (result.shopDomain && getDashboardSetupStatus(result, themeActivations).ready) {
                    try { localStorage.setItem(readyKey(result.shopDomain), 'true'); } catch { /* Optional wording hint. */ }
                }
            } catch {
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setChecked(true);
                }
            }
        };
        void load();
        return () => { cancelled = true; };
    }, [reload]);

    const status = result ? getDashboardSetupStatus(result.health, result.activations) : undefined;
    return {
        health, status, loading, failed,
        showCard: checked && (!status?.ready || failed),
        retry: () => setReload(value => value + 1),
        openThemeEditor: () => { themeEditorVisit.current = 'opening'; },
    };
}

export type DashboardSetupState = ReturnType<typeof useDashboardSetup>;
